import { Alert, Button, Card, Flex, Skeleton, theme, Typography } from 'antd';
import { FiPlus } from 'react-icons/fi';
import { useTheme } from 'styled-components';

import { fontSize, radius, space, themeTokens } from '@contexts/themeTokens';

import CollectionGridCard from './CollectionGridCard';

// A layout width, not a spacing step: the grid fits as many cards of at least this width as it can.
const CARD_MIN_WIDTH = '300px';

const SectionHeading = ({ label, hint }) => {
	const { colors } = useTheme();

	return (
		<Typography.Text
			style={{
				fontSize: fontSize('3xs'),
				letterSpacing: space(1),
				color: colors.textAccent,
			}}>
			<span style={{ fontWeight: themeTokens.typography.weights.bold }}>{label}</span>
			{hint ? ` ${hint}` : null}
		</Typography.Text>
	);
};

const Muted = ({ children, padding }) => {
	const { colors } = useTheme();

	return (
		<Typography.Text
			style={{
				display: 'block',
				textAlign: 'center',
				padding,
				fontSize: fontSize('2xs'),
				color: colors.textSubtle,
			}}>
			{children}
		</Typography.Text>
	);
};

const Grid = ({ children }) => (
	<div
		style={{
			display: 'grid',
			gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${CARD_MIN_WIDTH}), 1fr))`,
			gap: space(5),
		}}>
		{children}
	</div>
);

const BuildCard = ({ onNew }) => {
	const { colors } = useTheme();
	const {
		token: { lineWidth },
	} = theme.useToken();

	return (
		<Card
			size="small"
			variant="borderless"
			style={{
				borderStyle: 'dashed',
				borderWidth: lineWidth,
				borderColor: colors.borderInput,
				borderRadius: radius(4),
			}}
			styles={{ body: { padding: space(5, 5) } }}>
			<Flex align="center" gap={space(5)} wrap>
				<Flex vertical flex={1} style={{ minWidth: 0 }}>
					<Typography.Text
						strong
						style={{ fontSize: fontSize('2xs'), color: colors.textSecondary }}>
						Build a new collection
					</Typography.Text>
					<Typography.Text
						style={{
							fontSize: fontSize('3xs'),
							color: colors.textAccent,
							lineHeight: 1.5,
						}}>
						Pick objects from the taxonomy tree and save them as a reusable set for
						detection and annotation.
					</Typography.Text>
				</Flex>
				<Button type="primary" icon={<FiPlus />} onClick={onNew}>
					New collection
				</Button>
			</Flex>
		</Card>
	);
};

/**
 * The Collections page's list mode: the build card, then Templates and the
 * viewer's own collections — the organization's while one is active, their
 * personal ones otherwise. Receives collections already filtered by project
 * type, scope and search, with their objects resolved against the taxonomy.
 *
 * @param {boolean} props.isSearching - hide empty sections instead of explaining them
 */
const CollectionsList = ({
	templates,
	owned,
	ownedHeading,
	ownedHint,
	isLoading,
	isError,
	isSearching,
	copyLabel,
	copyingId,
	copyDisabled = false,
	selectedId,
	onNew,
	onCopy,
	onSelect,
	onEdit,
	onDelete,
}) => {
	if (isLoading) return <Skeleton active paragraph={{ rows: 6 }} title={false} />;

	if (isError) {
		return (
			<Alert
				type="error"
				showIcon
				title="Couldn't load collections"
				description="Please try again later."
			/>
		);
	}

	const noMatches = isSearching && templates.length + owned.length === 0;

	return (
		<Flex vertical gap={space(8)}>
			<BuildCard onNew={onNew} />

			{noMatches && <Muted padding={space(10, 0)}>No collections match this search.</Muted>}

			{templates.length > 0 && (
				<Flex vertical gap={space(5)}>
					<SectionHeading
						label="TEMPLATES"
						hint="curated by Biodrone · copies are independent"
					/>
					<Grid>
						{templates.map(collection => (
							<CollectionGridCard
								key={collection.id}
								collection={collection}
								copyLabel={copyLabel}
								copying={collection.id === copyingId}
								copyDisabled={copyDisabled}
								onCopy={onCopy}
							/>
						))}
					</Grid>
				</Flex>
			)}

			{(owned.length > 0 || !isSearching) && (
				<Flex vertical gap={space(5)}>
					<SectionHeading label={ownedHeading} hint={ownedHint} />
					{owned.length > 0 ? (
						<Grid>
							{owned.map(collection => (
								<CollectionGridCard
									key={collection.id}
									collection={collection}
									selected={collection.id === selectedId}
									onSelect={onSelect}
									onEdit={onEdit}
									onDelete={onDelete}
								/>
							))}
						</Grid>
					) : (
						<Muted padding={space(6, 0)}>
							{templates.length > 0
								? 'No collections yet. Build one, or copy a template.'
								: 'No collections yet. Build one.'}
						</Muted>
					)}
				</Flex>
			)}
		</Flex>
	);
};

export default CollectionsList;
